import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AUTH_BG = "https://images.pexels.com/photos/11093512/pexels-photo-11093512.jpeg";

export default function RegisterCompanyPage() {
  const { t } = useLang();
  const [form, setForm] = useState({
    company_name: "",
    tax_no: "",
    phone: "",
    email: "",
    password: "",
    password2: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-company", {
        company_name: form.company_name,
        tax_no: form.tax_no,
        phone: form.phone,
        email: form.email,
        password: form.password,
      });
      setDone(data);
      toast.success("Kayıt alındı");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-white relative" data-testid="register-company-page">
      <div className="absolute top-6 right-6 z-20">
        <LanguageToggle />
      </div>
      <div className="hidden lg:block lg:col-span-3 relative auth-noise">
        <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A1128]/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Wordmark dark />
          <div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              {t("Firmanızı dakikalar içinde")}<br />
              <span className="text-blue-400">{t("TEMHA'ya taşıyın.")}</span>
            </h1>
            <ul className="mt-8 space-y-3 text-slate-300">
              <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-blue-400" /> {t("Onay beklemeden anında aktif")}</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-blue-400" /> {t("Verileriniz diğer firmalardan tamamen izole")}</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-blue-400" /> {t("Çift taraflı muhasebeye uygun hazır hesap planı")}</li>
            </ul>
            <div className="mt-8 rounded-md bg-blue-600/10 border border-blue-500/30 p-4 max-w-md" data-testid="register-subscription-teaser">
              <p className="text-sm text-blue-300">
                {t("Abonelik yakında — Iyzico ile aylık/yıllık planlar geliyor.")}
              </p>
            </div>
          </div>
          <p className="text-slate-400 text-sm">{t("© 2026 TEMHA — Çok firmalı finans yönetim platformu")}</p>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-8 sm:p-12 lg:p-16">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-10">
            <Wordmark />
          </div>

          {done ? (
            <div className="text-left" data-testid="register-success">
              <CheckCircle2 className="h-12 w-12 text-green-600 mb-6" />
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128]">
                {t("Kaydınız alındı")}
              </h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                <strong>{form.email}</strong> {t("adresine bir doğrulama bağlantısı gönderdik. E-postanızı doğruladıktan sonra firmanız aktif olur ve giriş yapabilirsiniz.")}
              </p>
              {!done.email_sent && (
                <p className="mt-3 text-sm text-amber-700">
                  {t("E-posta gönderilemedi.")} <Link to="/verify-email" className="underline font-medium">{t("Buradan yeniden gönderin")}</Link>.
                </p>
              )}
              <Button asChild className="mt-8 bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/login" data-testid="goto-login-button">{t("Giriş sayfasına dön")}</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="h-6 w-6 text-blue-600" />
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128]" data-testid="register-heading">
                  {t("Firma Kaydı")}
                </h2>
              </div>
              <p className="mt-2 text-sm text-slate-500">
                {t("Yeni firma oluşturun. Kayıt sonrası e-posta doğrulaması gereklidir.")}
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="register-company-form">
                <div className="space-y-2">
                  <Label htmlFor="company_name">{t("Firma Adı")}</Label>
                  <Input id="company_name" name="company_name" required value={form.company_name} onChange={set("company_name")}
                    placeholder="Örnek Ticaret A.Ş." data-testid="register-company-name-input" className="focus-visible:ring-blue-600" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tax_no">{t("Vergi No")}</Label>
                    <Input id="tax_no" name="tax_no" required value={form.tax_no} onChange={set("tax_no")}
                      placeholder="10-11 haneli" minLength={10} maxLength={11}
                      data-testid="register-tax-no-input" className="focus-visible:ring-blue-600" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("Telefon")}</Label>
                    <Input id="phone" name="phone" type="tel" required value={form.phone} onChange={set("phone")}
                      placeholder="05xx xxx xx xx" data-testid="register-phone-input" className="focus-visible:ring-blue-600" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("Admin E-posta")}</Label>
                  <Input id="email" name="email" type="email" required value={form.email} onChange={set("email")}
                    placeholder="admin@firma.com" data-testid="register-email-input" className="focus-visible:ring-blue-600" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("Şifre")}</Label>
                  <Input id="password" name="password" type="password" required minLength={8} value={form.password} onChange={set("password")}
                    placeholder="En az 8 karakter" data-testid="register-password-input" className="focus-visible:ring-blue-600" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password2">{t("Şifre (Tekrar)")}</Label>
                  <Input id="password2" name="password_confirm" type="password" required minLength={8} value={form.password2} onChange={set("password2")}
                    placeholder="Şifrenizi doğrulayın" data-testid="register-password2-input" className="focus-visible:ring-blue-600" />
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200 hover:-translate-y-[1px]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Firmayı Oluştur")}
                </Button>
              </form>

              <p className="mt-8 text-sm text-slate-500">
                {t("Zaten hesabınız var mı?")}{" "}
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700" data-testid="goto-login-link">
                  {t("Giriş yapın")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
